import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { treeActions } from "../../../redux/slices/treeSlice";
import { api } from "../../../redux/api";
import { inboundTemplateToRcTree } from "../../../utils/rctreeDataConversion";
import { filterOutNodeTypes } from "../../../utils/utilFunctions";
import { loadWizardReturnContext } from "./apiServiceWizardSession";

function resolveInboundFromContext(templateHeader, ctx) {
  const dep = templateHeader?.DEPENDENCIES || "";
  const parts = dep.split("/");
  const workflowGroup = parts[0] || ctx?.translateData?.workflowGroup || "";
  const inboundTemplate = parts[2] || ctx?.translateData?.inboundTemplate || "";
  return { workflowGroup, inboundTemplate };
}

function isLoadedTemplateHeader(templateHeader) {
  return (
    templateHeader &&
    !Array.isArray(templateHeader) &&
    typeof templateHeader === "object"
  );
}

async function loadInboundDependency(dispatch, account, workflowGroup, inboundTemplate) {
  dispatch(treeActions.setCurrentScreen("outbound"));
  dispatch(
    treeActions.setInboundDependencyTree({
      data: {
        header: [],
        body: [],
        trailer: [],
        templateHeader: {},
        isLoading: true,
        templateFound: false,
      },
    })
  );

  const res = await dispatch(
    api.endpoints.getInboundTemplateData.initiate(
      {
        workflowGroupName: workflowGroup,
        templateName: inboundTemplate,
        account,
      },
      { forceRefetch: true }
    )
  );

  if (res?.data?.TEMPLATE) {
    const header = filterOutNodeTypes(
      inboundTemplateToRcTree(res.data, "HEADER", null, true),
      ["COMMENT"]
    );
    const body = filterOutNodeTypes(inboundTemplateToRcTree(res.data, "BODY"), ["COMMENT"]);
    const trailer = filterOutNodeTypes(
      inboundTemplateToRcTree(res.data, "TRAILER"),
      ["COMMENT"]
    );
    dispatch(
      treeActions.setInboundDependencyTree({
        data: {
          header,
          body,
          trailer,
          templateHeader: res.data?.TEMPLATE?.TEMPLATE_HEADER,
          isLoading: false,
          templateFound: true,
        },
      })
    );
    return true;
  }

  dispatch(
    treeActions.setInboundDependencyTree({
      data: {
        header: [],
        body: [],
        trailer: [],
        templateHeader: {},
        isLoading: false,
        templateFound: false,
      },
    })
  );
  return false;
}

/**
 * When opened from API Service wizard mapping button, select outbound template
 * and enable mapping mode once the template (and inbound dependency) is loaded.
 */
export default function ApiServiceWizardMappingLanding({ urlParams }) {
  const dispatch = useDispatch();
  const openMapping =
    urlParams?.openMapping === "1" || urlParams?.openMapping === "true";
  const account = useSelector((state) => state.queryParams.account);
  const templateHeader = useSelector(
    (state) => state.treeData.outbound.rightTree.tree.templateHeader
  );
  const rightLoading = useSelector((state) => state.treeData.outbound.rightTree.loading);
  const leftLoading = useSelector((state) => state.treeData.outbound.leftTree.loading);
  const inboundDependencyTree = useSelector(
    (state) => state.treeData.outbound.leftTree.inboundDependencyTree
  );
  const isMappingMode = useSelector((state) => state.treeData.mappingStates.isMappingMode);
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!openMapping || appliedRef.current || isMappingMode) return;
    if (rightLoading || leftLoading) return;
    // templateHeader defaults to [] until outbound template is fetched
    if (!isLoadedTemplateHeader(templateHeader)) return;

    const ctx = loadWizardReturnContext();
    const { workflowGroup, inboundTemplate } = resolveInboundFromContext(templateHeader, ctx);
    if (!workflowGroup || !inboundTemplate) return;

    const depValue = `${workflowGroup}/inbound_templates/${inboundTemplate}`;
    if (!templateHeader.DEPENDENCIES) {
      dispatch(
        treeActions.setRightTreeTemplateHeaders({
          screen: "outbound",
          key: "DEPENDENCIES",
          value: depValue,
        })
      );
    }

    const enableMapping = () => {
      appliedRef.current = true;
      dispatch(treeActions.setCurrentScreen("outbound"));
      dispatch(treeActions.setIsMappingMode(true));
    };

    if (inboundDependencyTree?.templateFound && !inboundDependencyTree?.isLoading) {
      enableMapping();
      return;
    }

    if (inboundDependencyTree?.isLoading) return;

    let cancelled = false;
    loadInboundDependency(dispatch, account, workflowGroup, inboundTemplate).then((ok) => {
      if (!cancelled && ok) enableMapping();
    });

    return () => {
      cancelled = true;
    };
  }, [
    openMapping,
    rightLoading,
    leftLoading,
    templateHeader,
    inboundDependencyTree?.templateFound,
    inboundDependencyTree?.isLoading,
    isMappingMode,
    account,
    dispatch,
  ]);

  return null;
}
